import { CategoryOutlined, DisplaySettings } from "@mui/icons-material";

const FACTOR_STRENGTH = 5; // Assuming this is a constant factor strength used in adjustments


export class ForecastUtils {

  static calculateCAGR(startValue: number, endValue: number, periods: number): number {
    if (typeof startValue !== "number" || typeof endValue !== "number" || typeof periods !== "number") {
      return 0
    }
    if (startValue === 0 || endValue === 0) return 0;
    return ((Math.pow(endValue / startValue, 1 / periods) - 1) * 100);
    }

  static calculateYoYChange(current: number, previous: number): number {
    if (previous === 0) {
      return 0; // Avoid division by zero
    }
    return ((current - previous) / previous); // Return percentage change
  }

  static alignWithFixed(value: number, total: number, fixed: number): number {
    if (total === 0) return 0; // Avoid division by zero
    if (fixed === 0) return 0; // Avoid division by zero
    if (value === 0) return 0; // Avoid division by zero
    const normalized = value / total;
    const aligned = normalized * fixed;
    return Math.round(aligned * 100) / 100; // Round to two decimal
  }
  static applyAdjustment(item: DataItem, value: number): DataItem {
    const adjustedItem = {...item};
    const factor = 1 + (value / FACTOR_STRENGTH); // Assuming value is a percentage adjustment
    adjustedItem.displayRevenue = item.initialRevenue * factor;
    adjustedItem.displayUnits = item.initialUnits * factor;
    return adjustedItem;
  }
}

export interface ForecastItem {
  id: string;
  year_offset: number;
  revenue: number;
  units: number;
}

export interface HistoryItem {
  id: string;
  yearOffset: number;
  revenue: number;
  units: number;
}


export interface Category {
  id: string;
  category_name: string;
  analysis: string;
  revenue: number;
  units_sold: number;
  forecasts: ForecastItem[];
  history: HistoryItem[];

}

export interface DataItem {
  yearOffset: number;
  id: string;
  categoryId: string;
  initialRevenue: number;
  initialUnits: number;
  displayRevenue: number;
  displayUnits: number;
  concentrationRevenue: number;
  concentrationUnits: number;
  initialYoYRevenue: number; // default YoY revenue for the item
  initialYoYUnits: number; // default YoY units for the item
  displayYoYRevenue: number; // display YoY revenue for the item
  displayYoYUnits: number; // display YoY units for the item
}

export interface TotalItem {
  yearOffset: number;
  revenue: number;
  units: number;
  yoyRevenue: number; // YoY revenue for the total item
  yoyUnits: number; // YoY units for the total item

}

export interface ForecastRow {
  id: string;
  categoryId: string;
  categoryName: string;
  [yearOffset: number]: DataItem;
  cagrRevenue: number ; // CAGR for revenue
  cagrUnits: number; // CAGR for units
  analysis?: string;
}
export interface ColumnAgg {
  sumDisplayRevenue?: number;
  sumDisplayUnits?: number;
}

export interface AdjustmentRow {
  id: string;
  categoryId: string;
  categoryName: string;
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
}

export interface TotalRow {
  id: string;
  categoryName: string;
  [yearOffset: number]: DataItem;
  cagrRevenue: number; // CAGR for revenue
  cagrUnits: number; // CAGR for units
}


export interface forecastState  {
  categories: Category[];
  forecastRows: Record<string, ForecastRow>;
  adjustmentRows: Record<string, AdjustmentRow>;
  totalRow: TotalRow;
  columnAgg: Record<number, ColumnAgg>;
  isAnchor: boolean;
  lastModified: Date;
}


const handleAdjustment = (row: ForecastRow, adjustmentValue: number, yearOffset: number): ForecastRow => {
    console.log("Handling adjustment for row", row.id, "yearOffset", yearOffset, "adjustmentValue", adjustmentValue);
    const newRow: ForecastRow = {...row};
    const item = newRow[yearOffset];
    var newItem = ForecastUtils.applyAdjustment(item, adjustmentValue);
    console.log("New item after adjustment", newItem);
    newRow[yearOffset] = ForecastUtils.applyAdjustment(item, adjustmentValue);
return newRow;
}


const aggregateColumn = (forecastRows: Record<string, ForecastRow>, yearOffset:number): ColumnAgg => {
    console.log("Aggregating column for yearOffset", yearOffset);
    let sumDisplayRevenue = 0;
    let sumDisplayUnits = 0;

    Object.values(forecastRows).forEach(row => {
      const item = row[yearOffset];
      if (!item) {
        console.warn(`No data for year offset ${yearOffset} in row ${row.id}`);
        throw new Error(`No data for year offset ${yearOffset} in row ${row.id}`);
      }
      sumDisplayRevenue += item.displayRevenue;
      sumDisplayUnits += item.displayUnits;
    });
    return {sumDisplayRevenue, sumDisplayUnits};
  }

const propagateRow = (row: ForecastRow, yearStart: number): ForecastRow => {
    console.log("Propagating row", row.id, "for yearOffset", yearStart);
    const newItems: {[key:string]: DataItem} = {};
    var previousItem = row[yearStart];
    for (let i = yearStart + 1; i <= 5; i++) {
        if (!previousItem) {
        console.warn(`No data for year offset ${i - 1} in row ${row.id}`);
        continue;
        }
        const currentItem = row[i];
        const newRevenue = previousItem.displayRevenue * (1 + currentItem.initialYoYRevenue )
        const newUnits = previousItem.displayUnits * (1 + currentItem.initialYoYUnits );
        const newItem = {...currentItem, displayRevenue: newRevenue, displayUnits: newUnits };
        previousItem = newItem;
        newItems[i] = newItem;
    }
    return {...row, ...newItems};
    };
const normalizeColumn = (forecastRows: Record<string, ForecastRow>, yearOffset: number, total: DataItem): Record<string, ForecastRow> => {
    console.log("Normalizing column for yearOffset", yearOffset);
  const colAgg = aggregateColumn(forecastRows, yearOffset);
  console.log("Column aggregation result", colAgg);
  const updatedRows: Record<string, ForecastRow> = {};
  Object.values(forecastRows).forEach(row => {
    const item = row[yearOffset];
    const previousItem = row[yearOffset - 1];

    if (!item) {
      console.warn(`No data for year offset ${yearOffset} in row ${row.id}`);
      throw new Error(`No data for year offset ${yearOffset} in row ${row.id}`);
    }
    if (!previousItem) {
      console.warn(`No data for year offset ${yearOffset - 1} in row ${row.id}`);
      throw new Error(`No data for year offset ${yearOffset - 1} in row ${row.id}`);
    }
    const newItem = {...item}
    newItem.displayRevenue = ForecastUtils.alignWithFixed(newItem.displayRevenue, colAgg.sumDisplayRevenue, total.displayRevenue);
    newItem.displayUnits = ForecastUtils.alignWithFixed(item.displayUnits, colAgg.sumDisplayUnits, total.displayRevenue);
    newItem.displayYoYRevenue = ForecastUtils.calculateYoYChange(newItem.displayRevenue, previousItem.displayRevenue);
    newItem.displayYoYUnits = ForecastUtils.calculateYoYChange(newItem.displayUnits, previousItem.displayUnits);
    console.log("New item after normalization", newItem);
    const newRow = {...row, [yearOffset]: newItem};
    if (yearOffset === 5) {
      // if this is the last forecast year we recalculate the CAGR
      newRow.cagrRevenue = ForecastUtils.calculateCAGR(newRow[0].displayRevenue, newRow[5].displayRevenue, 5);
      newRow.cagrUnits = ForecastUtils.calculateCAGR(newRow[0].displayUnits, newRow[5].displayUnits, 5);

    }
    updatedRows[row.id] = newRow
    console.log("Updated row ", newRow, "after normalizing colum", yearOffset, "total");
  });

  return updatedRows;

};

const normalizeColumns = (forecastRows: Record<string, ForecastRow>, totalRow: TotalRow, yearStart: number): Record<string, ForecastRow> => {
    console.log("Normalizing columns from yearStart", yearStart);
    const updatedRows: Record<string, ForecastRow> = {...forecastRows};
    for (let yearOffset = yearStart; yearOffset <= 5; yearOffset++) {
        const total = totalRow[yearOffset];
        const normalizedRows = normalizeColumn(updatedRows, yearOffset, total);
        Object.assign(updatedRows, normalizedRows);
    }
    return updatedRows;
};

const recalculateAnchorRow = (row: ForecastRow, yearOffset: number): ForecastRow => {

  const newRow: ForecastRow = {...row};
  for (let i = yearOffset + 1; i <= 5; i++) {
    const previousItem = row[i - 1];
    if (!previousItem) {
      console.warn(`No data for year offset ${i - 1} in row ${row.id}`);
      throw new Error(`No data for year offset ${i - 1} in row ${row.id}`);
    }
    const currentItem = row[i];
    if (!currentItem) {
      console.warn(`No data for year offset ${i} in row ${row.id}`);
      throw new Error(`No data for year offset ${i} in row ${row.id}`);
    }
    const newItem = {...currentItem};
    newItem.displayYoYRevenue = ForecastUtils.calculateYoYChange(newItem.displayRevenue, previousItem.displayRevenue)
    newItem.displayYoYUnits = ForecastUtils.calculateYoYChange(newItem.displayUnits, previousItem.displayUnits)
    newRow[i] = newItem;
  }
  newRow.cagrRevenue = ForecastUtils.calculateCAGR(newRow[0].displayRevenue, newRow[5].displayRevenue, 5);
  newRow.cagrUnits = ForecastUtils.calculateCAGR(newRow[0].displayUnits, newRow[5].displayUnits, 5);
  return newRow;
};

const updateTotals = (forecastRows: Record<string, ForecastRow>, totalRow: TotalRow): TotalRow => {
  // Calculate new totals based on the updated forecast rows
  const newTotals:TotalRow = {...totalRow};
  var prevRevenue = 0;
  var prevUnits = 0;
  for (let yearOffset = 1; yearOffset <= 5; yearOffset++) {
    const colAgg = aggregateColumn(forecastRows, yearOffset);
    const totalItem = totalRow[yearOffset];
    const newTotal = {
      ...totalItem,
      displayRevenue: colAgg.sumDisplayRevenue,
      displayUnits: colAgg.sumDisplayUnits,
      displayYoYRevenue: ForecastUtils.calculateYoYChange(colAgg.sumDisplayRevenue, prevRevenue),
      displayYoYUnits: ForecastUtils.calculateYoYChange(colAgg.sumDisplayUnits, prevUnits),
    };
    prevRevenue = newTotal.displayRevenue;
    prevUnits = newTotal.displayUnits;
    newTotals[yearOffset] = newTotal;
  }
  // Update the totals row CAGR values
  newTotals.cagrRevenue = ForecastUtils.calculateCAGR(newTotals[1].displayRevenue, newTotals[5].displayRevenue, 5);
  newTotals.cagrUnits = ForecastUtils.calculateCAGR(newTotals[1].displayUnits, newTotals[5].displayUnits, 5);
  return newTotals;
}
const recalculateCAGRs = (forecastRows: Record<string, ForecastRow>): Record<string, ForecastRow> => {
  const updatedRows: Record<string, ForecastRow> = {};
  Object.values(forecastRows).forEach(row => {
    const newRow = {...row};
    newRow.cagrRevenue = ForecastUtils.calculateCAGR(newRow[0].displayRevenue, newRow[5].displayRevenue, 5);
    newRow.cagrUnits = ForecastUtils.calculateCAGR(newRow[0].displayUnits, newRow[5].displayUnits, 5);
    updatedRows[row.id] = newRow;
  });
  return updatedRows;
}; 

const clearRows = (forecastRows: Record<string, ForecastRow>): Record<string, ForecastRow> => {
    const clearedRows: Record<string, ForecastRow> = {};
    Object.values(forecastRows).forEach(row => {
        const newRow: ForecastRow = {...row};
        for (let i = 1; i <= 5; i++) {
            const item = row[i];
            const newItem: DataItem = {...item}
            newItem.displayRevenue = item.initialRevenue;
            newItem.displayUnits = item.initialUnits;
            newItem.displayYoYRevenue = item.initialYoYRevenue;
            newItem.displayYoYUnits = item.initialYoYUnits;
            newRow[i] = newItem;
        }
        newRow.cagrRevenue = ForecastUtils.calculateCAGR(newRow[0].displayRevenue, newRow[5].displayRevenue, 5);
        newRow.cagrUnits = ForecastUtils.calculateCAGR(newRow[0].displayUnits, newRow[5].displayUnits, 5);
        clearedRows[row.id] = newRow;
    });
    return clearedRows;
};

export interface ForecastAdjustment {
  yearOffset: number;
  adjustmentValue: number;
  categoryId: string;
}
export interface ForecastActionPayload {
    adjustment?: ForecastAdjustment;
    categories?: Category[];
    forecastRows?: Record<string, ForecastRow>;
    adjustmentRows?: Record<string, AdjustmentRow>;
    totalRow?: TotalRow;
    columnAgg?: Record<number, ColumnAgg>;
    isAnchor?: boolean;
    lastModified?: Date;
}
export interface ForecastAction {
  type: 'UPDATE_FORECAST' | 'RESET_FORECAST' | 'INITIALIZE_FORECAST';
  payload: ForecastActionPayload;
}

export function forecastReducer(state: forecastState, action: ForecastAction): forecastState {
    switch (action.type) {
        case 'UPDATE_FORECAST':
            const { categoryId, yearOffset, adjustmentValue } = action.payload.adjustment;
            const currentRow = state.forecastRows[categoryId];
            if (!currentRow) {
                console.warn(`No forecast row found for categoryId ${categoryId}`);
                return state;
            }
            const newAdjustmentRow = {...state.adjustmentRows[categoryId]};
            newAdjustmentRow[yearOffset] = adjustmentValue;
            const updatedRow = handleAdjustment(currentRow, adjustmentValue, yearOffset);
            //console.log("Updated row after adjustment", updatedRow);
            const propagatedRow = propagateRow(updatedRow, yearOffset);
            //console.log("Propagated row after adjustment", propagatedRow);

            if (state.isAnchor) {
                const recalculatedRow = recalculateAnchorRow(propagatedRow, yearOffset);
                state.forecastRows[categoryId] = recalculatedRow;
                const updatedTotals = updateTotals(state.forecastRows, state.totalRow);
                return {
                    ...state,
                    forecastRows: {
                        ...state.forecastRows,
                        [categoryId]: recalculatedRow
                    },
                    totalRow: updatedTotals,
                    lastModified: new Date()
                };
            } else {
                const newRows = {...state.forecastRows, [categoryId]: propagatedRow };
                //console.log("New rows after adjustment", newRows);
                const normalizedRows = normalizeColumns(newRows, state.totalRow, yearOffset);
                //console.log("Normalized rows after adjustment", normalizedRows);
                return {
                    ...state,
                    forecastRows: {
                        ...normalizedRows,
                    },
                    lastModified: new Date(),
                    adjustmentRows: {
                        ...state.adjustmentRows,
                        [categoryId]: newAdjustmentRow
                    }
                };
            }
        case 'RESET_FORECAST':
            const initialForecastRows = clearRows(state.forecastRows);
            if (state.isAnchor) {
                const recalculatedTotals = updateTotals(initialForecastRows, state.totalRow);
                return {
                    ...state,
                    forecastRows: initialForecastRows,
                    totalRow: recalculatedTotals
                };
            } else {
                return {
                    ...state,
                    forecastRows: initialForecastRows
                };
            }

        case 'INITIALIZE_FORECAST':
            //const updatedForecastRows = recalculateCAGRs(action.payload.forecastRows);
            console.log("Initializing forecast with payload", action.payload.forecastRows);
            
            
            return {
                ...state,
                categories: action.payload.categories || state.categories,
                forecastRows: action.payload.forecastRows || state.forecastRows,
                adjustmentRows: action.payload.adjustmentRows,
                totalRow: action.payload.totalRow,
                columnAgg: action.payload.columnAgg,
                isAnchor: action.payload.isAnchor,
                lastModified: action.payload.lastModified
            };
            

        default:
            return state;
    }
}